<template>
    <div class="timepicker input-component" :class="cssClasses">
        <v-btn
            variant="plain"
            :ripple="false"
            :disabled="disabled || minDisabled"
            class="timepicker__button timepicker__button--left"
            @click.prevent.stop="decreaseTime"
        >
            <v-icon icon="mdi-chevron-left" size="large" />
        </v-btn>
        <v-text-field
            ref="inputRef"
            v-bind="props"
            v-model="inputValue"
            v-model:focused="focused"
            v-maska="maskOptions"
            :type="isMobileDevice ? 'time' : undefined"
            :min="min"
            :max="max"
            :active="focused"
            @focus="onFocus"
            @blur="onBlur"
            @change="onChange"
        >
            <template #prepend-inner>
                <div class="timepicker__spacer"></div>
            </template>
            <template #append-inner>
                <div class="timepicker__spacer"></div>
            </template>
            <template v-if="isMobileDevice" #default>
                <div class="timepicker__mobile-display">{{ label }}</div>
            </template>
        </v-text-field>
        <v-btn
            variant="plain"
            :ripple="false"
            :disabled="disabled || maxDisabled"
            class="timepicker__button timepicker__button--right"
            @click.prevent.stop="increaseTime"
        >
            <v-icon icon="mdi-chevron-right" size="large" />
        </v-btn>
    </div>
</template>

<script setup lang="ts">
import { addMinutes, formatDate, isAfter, isBefore, isEqual, isValid, parse, set } from 'date-fns';
import { computed, nextTick, ref, shallowRef } from 'vue';
import { useBaseInputComponent, type BaseInputComponentProps } from '../BaseInputComponent';
import { type VTextField } from 'vuetify/components';
import { vMaska } from 'maska/vue';
import type { MaskInputOptions } from 'maska';
import { syncRefs } from '@vueuse/core';

interface Props extends BaseInputComponentProps {
    /**
     * Format of the time picker
     * @default 'ampm'
     */
    format?: 'ampm' | '24hr';

    /**
     * Time increment/decrement step in minutes
     * @default 30
     */
    stepInMinutes?: number;

    /**
     * Minimum time
     */
    min?: string;

    /**
     * Maximum time
     */
    max?: string;
}

const props = withDefaults(defineProps<Props>(), { format: 'ampm', stepInMinutes: 30, density: 'default' });
const modelValue = defineModel<Date | number>({ required: true, default: () => new Date() });
const localModelValue = ref(modelValue.value);
const focused = defineModel<boolean>('focused');
const timeFormat = computed(() => (props.format === 'ampm' ? 'h:mm a' : 'HH:mm'));
const maskOptions = computed<MaskInputOptions>(() => ({
    // mobile devices must use HH:mm format for the native time input to work
    mask: props.format === '24hr' || isMobileDevice.value ? 'H#:m#' : 'h9:m# AM',
    tokens: {
        H: {
            pattern: /([0-2])/
        },
        m: {
            pattern: /[0-5]/,
            repeat: true
        },
        h: {
            pattern: /[1]/,
            optional: true
        },
        9: {
            pattern: /[0-9]/,
            optional: true
        },
        A: {
            pattern: /(A|P)/,
            transform: (chr: string) => chr.toUpperCase()
        }
    },
    eager: false,
    postProcess: (value: string) => {
        // post process the value if the format is not 24hr and the device is not mobile
        if (props.format !== '24hr' && !isMobileDevice.value) {
            // if first character is 1, and second character is more than 2, then add a colon in between
            if (value[0] === '1' && value[1] > '2') {
                value = value.slice(0, 1) + ':' + value.slice(1);
            }

            // remove all colons after the first one
            value = value.slice(0, value.indexOf(':') + 1) + value.slice(value.indexOf(':') + 1).replaceAll(':', '');
        }

        return value;
    }
}));
const { isMobileDevice, isIOS, cssClasses: baseCssClasses, watchInputClass } = useBaseInputComponent(props);
const cssClasses = computed(() => ({
    ...baseCssClasses.value,
    'input-component--focused': focused.value
}));
const timeValue = computed({
    get() {
        return formatDate(localModelValue.value, isMobileDevice.value ? 'HH:mm' : timeFormat.value);
    },
    set(value) {
        // parse string to Date
        const parsedDate = parse(value, isMobileDevice.value ? 'HH:mm' : timeFormat.value, localModelValue.value);
        if (isValid(parsedDate) && isWithinAllowedRange(parsedDate)) {
            localModelValue.value = set(localModelValue.value, {
                hours: parsedDate.getHours(),
                minutes: parsedDate.getMinutes(),
                seconds: 0,
                milliseconds: 0
            });
        } else {
            // reset the input value to the current time value
            inputValue.value = timeValue.value;
        }
    }
});
const label = computed(() => formatDate(localModelValue.value, timeFormat.value));
const inputRef = ref<InstanceType<typeof VTextField>>();
const inputValue = shallowRef(timeValue.value);

// ensure that localModelValue is always in sync with modelValue
syncRefs(modelValue, localModelValue);
// ensure that inputValue is always in sync with timeValue
syncRefs(timeValue, inputValue);

const parsedMin = computed(() => props.min && parse(props.min, 'HH:mm', modelValue.value));
const parsedMax = computed(() => props.max && parse(props.max, 'HH:mm', modelValue.value));
const minDisabled = computed(
    () =>
        !!(
            parsedMin.value &&
            isValid(parsedMin.value) &&
            (isEqual(modelValue.value, parsedMin.value) || isBefore(modelValue.value, parsedMin.value))
        )
);
const maxDisabled = computed(
    () =>
        !!(
            parsedMax.value &&
            isValid(parsedMax.value) &&
            (isEqual(modelValue.value, parsedMax.value) || isAfter(modelValue.value, parsedMax.value))
        )
);

// check if the input value is within the min and max times
function isWithinAllowedRange(value: Date) {
    // compare input value with min and max times
    if (parsedMin.value) {
        if (isValid(parsedMin.value) && isBefore(value, parsedMin.value)) return false;
    }

    if (parsedMax.value) {
        if (isValid(parsedMax.value) && isAfter(value, parsedMax.value)) return false;
    }

    return true;
}

function adjustTime(direction: 'increase' | 'decrease') {
    const currentDate = new Date(modelValue.value);
    const step = props.stepInMinutes;
    const currentMinutes = currentDate.getMinutes();
    const remainder = currentMinutes % step;

    const adjustment =
        direction === 'decrease'
            ? -(remainder || step) // Move to previous step
            : step - remainder; // Move to next step

    const newTime = addMinutes(currentDate, adjustment);
    const clampedTime = set(newTime, { seconds: 0, milliseconds: 0 });

    if (isValid(clampedTime) && isWithinAllowedRange(clampedTime)) {
        modelValue.value = clampedTime;
    }
}

function decreaseTime() {
    adjustTime('decrease');
}

function increaseTime() {
    adjustTime('increase');
}

function onFocus() {
    // update the input value for all other devices
    inputValue.value = timeValue.value;
}

// on iOS, this event is fired when the native time picker is closed after user taps on the "Done" button, so we update the model value here
function onBlur() {
    if (!isIOS.value) return;

    // update model value
    nextTick(() => (modelValue.value = localModelValue.value));
}

// on Android, this event is fired after user taps on the "Set" button of the native time picker, but the input will remain focused
// on iOS, this event is continuously fired when the native time picker value is changed while open
function onChange() {
    // update the input value for all other devices
    timeValue.value = inputValue.value;

    // do nothing on iOS because this will be handled by the onBlur event
    if (isIOS.value) return;

    // update model value
    nextTick(() => (modelValue.value = localModelValue.value));
}

watchInputClass(computed(() => inputRef.value?.$el));
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'vuetify/lib/components/VInput/_variables';

.timepicker {
    --control-height: #{$input-control-height};
    --v-input-padding-top: 8px;
    --v-button-width: 32px;

    // we need to fetch control height value from vuetify variables
    @each $density, $multiplier in $input-density {
        &:has(.v-input--density-#{$density}) {
            --control-height: #{$input-control-height + $multiplier * $spacer};
        }
    }

    position: relative;

    .v-field__input {
        text-align: center;
    }

    &__button {
        --v-btn-height: var(--control-height);

        position: absolute;
        top: 0;
        z-index: 1;
        padding: 0;
        min-width: 0;
        width: var(--v-button-width);
        padding-top: var(--v-field-input-padding-top);
        padding-bottom: var(--v-field-input-padding-bottom);

        &--left {
            left: 0;
        }

        &--right {
            right: 0;
        }
    }

    &__spacer {
        width: calc(var(--v-button-width) - 12px);
    }

    // hide the input field when the mobile display is active
    // but retain the input field for native mobile time input
    &:has(.timepicker__mobile-display) .v-field__input input {
        opacity: 0;
    }

    // facade for the mobile display to make it looks like a normal input field
    &__mobile-display {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
        pointer-events: none;
        min-height: max(var(--control-height, 56px), 1.5rem + var(--v-field-input-padding-top) + var(--v-field-input-padding-bottom));
        min-width: 0;
        padding-inline: var(--v-field-padding-start) var(--v-field-padding-end);
        padding-top: var(--v-field-input-padding-top);
        padding-bottom: var(--v-field-input-padding-bottom);
    }
}
</style>
